import { strings } from "@perceptron/constants";
import { type IconName } from "@perceptron/ui/components/icon";
import { parseAsStringEnum } from "nuqs/server";

/**
 * High-level demo modes. The active mode is the source of truth in the `?mode=`
 * query param and selects which surface is rendered. An absent or invalid value
 * falls back to {@link DEFAULT_MODE}.
 */
export const MODES = ["compose", "detect"] as const;

export type Mode = (typeof MODES)[number];

export const DEFAULT_MODE: Mode = "compose";

/** Query-param key shared by the client hook and the server-side parser cache. */
export const MODE_QUERY_KEY = "mode";

/** nuqs parser: isomorphic, validates membership, falls back to the default. */
export const modeParser = parseAsStringEnum<Mode>([...MODES]).withDefault(DEFAULT_MODE);

/** Presentation metadata for a mode: header trigger + selector card content. */
export interface ModeMeta {
  /** Display name, e.g. "Compose". */
  label: string;
  /** Card description copy. */
  description: string;
  /** Header trigger icon (must exist in the UI ICON_MAP). */
  icon: IconName;
  /** "Docs" link target for the card. */
  docsUrl: string;
  /** Optional looping preview video for the card hero. */
  previewVideo?: string;
}

/**
 * Per-mode metadata. Keyed by {@link Mode}, so adding a new mode to {@link MODES}
 * forces a matching entry here and the selector picks it up automatically.
 */
export const MODE_META: Record<Mode, ModeMeta> = {
  compose: {
    label: strings.modeSwitcher.modes.compose.label,
    description: strings.modeSwitcher.modes.compose.description,
    icon: "pencil-line",
    docsUrl: "https://docs.perceptron.inc",
  },
  detect: {
    label: strings.modeSwitcher.modes.detect.label,
    description: strings.modeSwitcher.modes.detect.description,
    icon: "locate",
    docsUrl: "https://docs.perceptron.inc/capabilities/detect",
    previewVideo: "/demo/assets/videos/detect-card-animation.mp4",
  },
};
